import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Fragment, useEffect, useState } from 'react';
import { Alert, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import useWidgetStore from "../../store/useWidgetStore";
import { CustomSelectElement } from '../../layouts/Theme';
import { useFormContext } from 'react-hook-form';
import { useI18N } from '../../context/I18NContext';
import AmountSelect from '../actions/AmountSelect';
export default function CapacityPicker(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    const { selectedCapacity, selectedCapacityGroups, selectedResource, setSelectedCapacity, setSelectedCapacityGroups, setSelectedCapacityVisible } = useWidgetStore();
    // @ts-ignore
    // props.config.required = true
    // how many values a range offers. Both render paths below only put a select on screen when
    // this is more than one, so it is also what decides whether there is anything to answer.
    // `??`, not a truthy check — a min or max of 0 is a real bound, not a missing one, and the
    // truthy check silently swapped it for 1, undercounting a 0-or-1 range down to one option.
    const optionCount = (min, max) => (max !== null && max !== void 0 ? max : 99) + 1 - (min !== null && min !== void 0 ? min : 1);
    // Whether the consumer has a capacity control in front of them.
    const hasVisibleControl = () => {
        var _a;
        if (!((_a = props.config) === null || _a === void 0 ? void 0 : _a.rules)) {
            // no rules means no `multiple` plugin on this apptype (or no apptype picked yet):
            // neither path renders a control, so there is no question to put to the consumer
            return false;
        }
        if (props.config.rules.length === 0) {
            // one amount select for the appointment as a whole
            return optionCount(props.config.min, props.config.max) > 1;
        }
        // one select per capacity group, each rendered only when its own range offers a choice
        return props.config.rules.some((rule) => optionCount(rule.minCapacity, rule.maxCapacity) > 1);
    };
    const visibleControl = hasVisibleControl();
    // The only record of whether the consumer actually saw a capacity control for this apptype -
    // selectedCapacity/selectedCapacityGroups get a value regardless (a silent default of 1 when
    // there is nothing to answer), so without this a summary can't tell "1 Kinderen, chosen" apart
    // from "1, the only possible answer, never shown on screen".
    useEffect(() => {
        setSelectedCapacityVisible(visibleControl);
    }, [visibleControl]);
    // The consumer has a control in front of them and the view says they have to use it, so
    // nothing may answer on their behalf - not the capacity, not the capacity groups, and not the
    // value the select shows.
    const awaitingAnswer = !!(((_a = props.config) === null || _a === void 0 ? void 0 : _a.required) && visibleControl);
    // Capacity 0 means "waiting for the consumer to say how many people": it disables the date and
    // time queries and puts "Kies eerst aantal personen" under the calendar. `required` in the
    // view config is what asks for that wait, and it is a widget-wide setting - so it can only
    // mean something while there is a control on screen to answer. With no picker visible the
    // capacity is not a question at all, and the value underneath is the single possible answer.
    //
    // Keyed on what the apptype makes of this picker, not on `required` alone: `required` comes
    // from the YAML and never changes, so this ran exactly once - on the first render, before an
    // apptype was picked, when min and max are still undefined and the option count works out to
    // 99. That is how an apptype without the plugin ended up waiting for an answer it never asked
    // for, and why the date picker never got past "Kies eerst aantal personen".
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        if (!visibleControl) {
            setSelectedCapacity((_b = (_a = props.config) === null || _a === void 0 ? void 0 : _a.min) !== null && _b !== void 0 ? _b : 1);
            return;
        }
        setSelectedCapacity(((_c = props.config) === null || _c === void 0 ? void 0 : _c.required) ? 0 : ((_e = (_d = props.config) === null || _d === void 0 ? void 0 : _d.min) !== null && _e !== void 0 ? _e : 1));
    }, [visibleControl, (_b = props.config) === null || _b === void 0 ? void 0 : _b.required, (_c = props.config) === null || _c === void 0 ? void 0 : _c.min, (_d = props.config) === null || _d === void 0 ? void 0 : _d.max]);
    const theme = useTheme();
    const { t, ts } = useI18N();
    const { setValue } = useFormContext();
    const isLarge = useMediaQuery(theme.breakpoints.up('sm'));
    const [values, setValues] = useState({});
    const [showMinError, setShowMinError] = useState(false);
    const [showMaxError, setShowMaxError] = useState(false);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        if (((_b = (_a = props.config) === null || _a === void 0 ? void 0 : _a.rules) === null || _b === void 0 ? void 0 : _b.length) === 0 && !props.config.required) {
            setSelectedCapacity((_d = (_c = props.config) === null || _c === void 0 ? void 0 : _c.min) !== null && _d !== void 0 ? _d : 1);
            setSelectedCapacityGroups({ 0: (_f = (_e = props.config) === null || _e === void 0 ? void 0 : _e.min) !== null && _f !== void 0 ? _f : 1 });
        }
    }, [(_e = props.config) === null || _e === void 0 ? void 0 : _e.rules]);
    useEffect(() => {
        var _a, _b;
        if (((_b = (_a = props.config) === null || _a === void 0 ? void 0 : _a.rules) === null || _b === void 0 ? void 0 : _b.length) === 0) {
            // we only have multiple person, but no specific groups
            // set the "group" too, as it is used by prepare and uppdate cart to determine price and capacity
            // in some cases, and not just the selectedCapacity ( I have no clue what's used when, but we need to post 
            // both capacity: number AND capacities: { 0 : number } in order to not throw exceptions from the cart form
            setSelectedCapacityGroups({ 0: selectedCapacity });
        }
    }, [selectedCapacity]);
    // Declared above the early return below, together with its effects: hooks may not sit behind
    // a return that an apptype can flip. An apptype whose `multiple` plugin has no capacity groups
    // renders the single amount select and took that return, and React threw "Rendered fewer hooks
    // than expected" the moment it was picked - the select view went blank.
    const updateValues = () => {
        var _a;
        // groups only. With no rules, or rules and no groups in them, the effects above own the
        // capacity and there is nothing here to prefill.
        if (((_a = props.config) === null || _a === void 0 ? void 0 : _a.rules) && props.config.rules.length > 0) {
            if (awaitingAnswer) {
                // Every select starts genuinely empty - undefined, never 0. 0 used to double as
                // "nothing picked yet", but it is a real, selectable answer for a group (0
                // kinderen is a valid choice), so it can no longer also mean "unanswered" without
                // making a real answer of 0 permanently unrepresentable.
                props.config.rules.forEach((rule) => setValue(`amount-${rule.groupId}`, undefined));
                // `values`/the error flags are this component's own running total across every
                // group, not react-hook-form's - resetting the fields above doesn't touch them.
                // Left alone, a second appointment (e.g. "Nog een afspraak toevoegen") starts its
                // selects visibly empty but onChange still adds the new pick on top of the FIRST
                // appointment's totals underneath, so picking 3 in one now-empty-looking select
                // right after a previous 1+1 reads as 5 and trips the max-persons error for a
                // number nobody can see on screen.
                setValues({});
                setShowMinError(false);
                setShowMaxError(false);
                // The store's capacity is the SAME "unanswered" signal the effect above uses (0 =
                // waiting for the consumer, disables the date/time query, shows "Kies eerst aantal
                // personen") - it has to go back to that here too. Left at whatever the FIRST
                // appointment answered, the date picker reads "capacity > 0" as already answered
                // and stays clickable while the selects on screen still show empty, so picking a
                // date/time before touching them silently books the previous appointment's
                // capacity again under a screen that never confirmed anything.
                setSelectedCapacity(0);
                setSelectedCapacityGroups({});
                return;
            }
            let total = 0, groups = {};
            props.config.rules.forEach((rule, index) => {
                var _a;
                const min = (_a = rule.minCapacity) !== null && _a !== void 0 ? _a : 0;
                // The first group is the one exception to "start on the minimum": the widget
                // needs a real, non-zero total to query dates/times with as soon as it stops
                // waiting for an answer, so it starts on this group's first non-zero option
                // instead. Every other group is free to start on 0 when that is one of its own
                // options - it no longer reads as "unanswered" the way it used to.
                const initial = index === 0 ? (min > 0 ? min : 1) : min;
                setValue(`amount-${rule.groupId}`, initial);
                total += initial;
                groups[rule.groupId] = initial;
            });
            setValue('amount', total);
            setSelectedCapacity(total);
            if (selectedCapacityGroups) {
                let _total = 0;
                for (const [_key, _value] of Object.entries(selectedCapacityGroups)) {
                    if (_key !== '0') {
                        setValue(`amount-${_key}`, _value);
                        _total += _value;
                        groups[parseInt(_key)] = _value;
                    }
                }
                // no group values yet is not an answer of 0 people: leave the capacity where the
                // effect above put it, which is 0 while we wait for the consumer and the minimum
                // when the view does not make them pick
                if (_total > 0) {
                    setValue('amount', _total);
                    setSelectedCapacity(_total);
                }
            }
            setValues(groups);
            // The minimums are what the selects start on, not something the consumer said. Handing
            // them to the store as capacity groups answers the question on their behalf: the next
            // pass through here - the resource select settling on its default is enough - reads
            // them back as a total and the wait is over before anything was picked. `onChange`
            // writes them for real once the consumer does pick.
            setSelectedCapacityGroups(groups);
        }
    };
    useEffect(() => {
        updateValues();
    }, []);
    useEffect(() => {
        updateValues();
    }, [selectedResource]);
    // the amount, or the prompt while there is no answer yet. 0 is a real, selectable answer now
    // (see updateValues), so only a genuinely unset value (undefined/null, parsing to NaN) reads
    // as unanswered — not 0.
    const renderSelectedValue = (selected) => {
        let amount = parseInt(`${selected}`);
        if (isNaN(amount)) {
            return (_jsx(Typography, { component: "span", sx: { color: theme.palette.text.secondary }, children: t('Selecteer aantal personen', "select") }));
        }
        return _jsx(Typography, { component: "span", children: amount });
    };
    if (((_g = (_f = props.config) === null || _f === void 0 ? void 0 : _f.rules) === null || _g === void 0 ? void 0 : _g.length) === 0) {
        return (_jsx(_Fragment, { children: _jsx(AmountSelect, { required: (_j = (_h = props.config) === null || _h === void 0 ? void 0 : _h.required) !== null && _j !== void 0 ? _j : false, value: selectedCapacity, setter: setSelectedCapacity, min: (_k = props.config) === null || _k === void 0 ? void 0 : _k.min, max: (_l = props.config) === null || _l === void 0 ? void 0 : _l.max, label: t((_o = (_m = props.config) === null || _m === void 0 ? void 0 : _m.label) !== null && _o !== void 0 ? _o : 'Aantal'), placeholder: t('Selecteer aantal personen', "select") }) }));
    }
    // the rest is only relevant if we have groups defined
    // also, the required config option isn't relevant when groups are defined, as those
    // selects include 0s ( unless they have a min value ) and therefore the manual
    // selection requirement is implicit
    const onChange = (value) => {
        var _a, _b, _c, _d;
        let _value = value.target, groupId = parseInt(_value.name.substring(7)), _values = values;
        _values[groupId] = _value.value;
        let _total = 0;
        for (const [_key, _value] of Object.entries(_values)) {
            _total += _value;
        }
        let minError = _total < ((_b = (_a = props.config) === null || _a === void 0 ? void 0 : _a.min) !== null && _b !== void 0 ? _b : 1), maxError = _total > ((_d = (_c = props.config) === null || _c === void 0 ? void 0 : _c.max) !== null && _d !== void 0 ? _d : 100);
        setShowMinError(minError);
        setShowMaxError(maxError);
        if (!minError && !maxError) {
            setValues(_values);
            setSelectedCapacityGroups(_values);
            setValue('amount', _total);
            setSelectedCapacity(_total);
        }
        else {
            // this disables the date and time queries
            setSelectedCapacity(0);
        }
    };
    return (_jsxs(_Fragment, { children: [(_q = (_p = props.config) === null || _p === void 0 ? void 0 : _p.rules) === null || _q === void 0 ? void 0 : _q.map((rule, i) => {
                var _a, _b, _c, _d;
                let group = (_b = (_a = props.config) === null || _a === void 0 ? void 0 : _a.groups) === null || _b === void 0 ? void 0 : _b.find((group) => group.id === rule.groupId);
                let options = [];
                for (let i = ((_c = rule.minCapacity) !== null && _c !== void 0 ? _c : 1); i < (rule.maxCapacity ? rule.maxCapacity + 1 : 100); i++) {
                    options.push({ id: i, label: i.toString() });
                }
                return (_jsx(Fragment, { children: options.length > 1 && (_jsx(Box, { sx: { minWidth: 120, my: 1, maxWidth: isLarge ? '500px' : '100%' }, children: _jsx(CustomSelectElement, { id: `amount-${group === null || group === void 0 ? void 0 : group.id}`, label: t((_d = group === null || group === void 0 ? void 0 : group.name) !== null && _d !== void 0 ? _d : "Aantal", "select"), 
                            // Neither prop actually drives what shows: CustomSelectElement
                            // (react-hook-form-mui's SelectElement) always displays its
                            // own react-hook-form-registered value instead, which
                            // updateValues() above already sets correctly via setValue().
                            // Passing a `defaultValue` here alongside RHF's `value` is a
                            // conflicting pair MUI treats inconsistently for a falsy (0)
                            // value specifically — the exact bug this was.
                            name: `amount-${group === null || group === void 0 ? void 0 : group.id}`, inputProps: {
                                //@ts-ignore
                                onChange: onChange
                            }, options: options, 
                            // the prompt occupies the value slot, so the label stays
                            // shrunk instead of dropping on top of it
                            InputLabelProps: { shrink: true }, SelectProps: {
                                // MUI skips renderValue while the field is empty unless it
                                // is told the empty state is worth rendering
                                displayEmpty: true,
                                renderValue: renderSelectedValue
                            } }) })) }, i));
            }), showMinError && (_jsx(Alert, { sx: { my: 2, mx: 1 }, severity: "warning", children: _jsx(Typography, { children: ts('Selecteer ten minste %amount% %amount=1?persoon:personen%', { amount: (_s = (_r = props.config) === null || _r === void 0 ? void 0 : _r.min) !== null && _s !== void 0 ? _s : 1 }, "select") }) })), showMaxError && (_jsx(Alert, { sx: { my: 2, mx: 1 }, severity: "warning", children: _jsx(Typography, { children: ts('Selecteer maximaal %amount% %amount=1?persoon:personen%', { amount: (_u = (_t = props.config) === null || _t === void 0 ? void 0 : _t.max) !== null && _u !== void 0 ? _u : 1 }, "select") }) }))] }));
}
